import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { notificationAPI } from '../../services/api';
import useNotificationCount from '../../hooks/useNotificationCount';
import toast from 'react-hot-toast';
import { formatDateTime } from '../../utils/dateTime';

const TYPE_LABELS = {
  DEAL_REQUEST: 'Deal Request',
  DEAL_APPROVED: 'Deal Approved',
  DEAL_REJECTED: 'Deal Rejected',
  ORDER_SUBMITTED: 'Order Submitted',
  ORDER_STATUS: 'Order Update',
  REFUND_SUBMITTED: 'Refund Submitted',
  REFUND_REMINDER: 'Refund Reminder',
  REFUND_PENALTY_50: '50% Penalty Warning',
  REFUND_PENALTY_100: '100% Penalty Warning',
  REFUND_REMINDER_MEDIATOR: 'Buyer Refund Pending',
  REFUND_PENALTY_50_MEDIATOR: 'Buyer Refund Overdue',
  REFUND_PENALTY_100_MEDIATOR: 'Buyer Refund Critical',
  NEW_ORDER: 'New Order',
};

const TYPE_COLORS = {
  DEAL_REQUEST: { bg: '#eef2ff', color: '#667eea' },
  DEAL_APPROVED: { bg: '#ecfdf5', color: '#10b981' },
  DEAL_REJECTED: { bg: '#fef2f2', color: '#ef4444' },
  ORDER_SUBMITTED: { bg: '#eef2ff', color: '#667eea' },
  ORDER_STATUS: { bg: '#fffbeb', color: '#f59e0b' },
  REFUND_SUBMITTED: { bg: '#ecfdf5', color: '#10b981' },
  REFUND_REMINDER: { bg: '#fffbeb', color: '#f59e0b' },
  REFUND_PENALTY_50: { bg: '#fef2f2', color: '#ef4444' },
  REFUND_PENALTY_100: { bg: '#fef2f2', color: '#dc2626' },
  NEW_ORDER: { bg: '#eef2ff', color: '#667eea' },
};

const NotificationsPage = ({ title = 'Notifications' }) => {
  const navigate = useNavigate();
  const { unreadCount, refreshUnreadCount } = useNotificationCount();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getAll();
      const list = res.data?.data || res.data || [];
      setNotifications(Array.isArray(list) ? list : []);
    } catch {
      toast.error('Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleClick = async (notif) => {
    if (!notif.readStatus) {
      try {
        await notificationAPI.markAsRead(notif.id);
        refreshUnreadCount();
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, readStatus: true } : n))
        );
      } catch {
        // continue
      }
    }
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      refreshUnreadCount();
      setNotifications((prev) => prev.map((n) => ({ ...n, readStatus: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark notifications as read');
    }
  };

  const filtered = notifications.filter((n) => {
    if (filter === 'unread') return !n.readStatus;
    if (filter === 'read') return n.readStatus;
    return true;
  });

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1e293b' }}>
              {title}
            </h2>
            {unreadCount > 0 && (
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {unreadCount > 0 && (
              <Button variant="secondary" size="sm" onClick={handleMarkAllRead}>
                Mark all read
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={fetchNotifications} icon="refresh">
              Refresh
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {['all', 'unread', 'read'].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                background: filter === f ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#ffffff',
                color: filter === f ? '#ffffff' : '#64748b',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
            Loading notifications...
          </div>
        ) : filtered.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '64px 24px',
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
            }}
          >
            <Icon name="bell" size={48} />
            <p style={{ margin: '16px 0 0', fontSize: '16px', color: '#64748b' }}>
              {filter === 'all' ? 'No notifications yet' : `No ${filter} notifications`}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filtered.map((notif) => {
              const typeStyle = TYPE_COLORS[notif.type] || { bg: '#f1f5f9', color: '#64748b' };
              return (
                <button
                  key={notif.id}
                  type="button"
                  onClick={() => handleClick(notif)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '16px 20px',
                    background: notif.readStatus ? '#ffffff' : '#f8faff',
                    border: `1px solid ${notif.readStatus ? '#e2e8f0' : '#c7d2fe'}`,
                    borderRadius: '12px',
                    cursor: notif.actionUrl ? 'pointer' : 'default',
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'flex-start',
                    transition: 'box-shadow 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (notif.actionUrl) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      marginTop: '6px',
                      flexShrink: 0,
                      background: notif.readStatus ? '#e2e8f0' : '#667eea',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                        {notif.title}
                      </span>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: typeStyle.bg,
                          color: typeStyle.color,
                        }}
                      >
                        {TYPE_LABELS[notif.type] || notif.type}
                      </span>
                    </div>
                    <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#475569', lineHeight: 1.5 }}>
                      {notif.message}
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                      {formatDateTime(notif.createdAt, '')}
                    </p>
                  </div>
                  {notif.actionUrl && (
                    <Icon name="ExternalLink" size={16} style={{ color: '#94a3b8', marginTop: '4px', flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
  );
};

export default NotificationsPage;
